import { useEffect, useRef } from "react";
import { ReactNode } from "react";
import styles from "./styles.module.scss";
import useIsMobile from "@app/hooks/useIsMobile";

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  showCloseBtn?: boolean;
  modalClass?: string;
  innerModalClass?: string;
  modalBodyStyle?: string;
  showFullScreen?: boolean;
  closeOnClickOutside?: boolean;
  style?: any;
  showCloseBtnNoFullScreen?: boolean;
  customCloseBtnClass?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen = false,
  onClose = () => {},
  children,
  showCloseBtn = false,
  modalClass,
  innerModalClass,
  modalBodyStyle,
  showFullScreen = true,
  closeOnClickOutside = true,
  style = {},
  showCloseBtnNoFullScreen = false, //forcefully show close button when not in full screen
  customCloseBtnClass = "", //for custom class of close button
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        closeOnClickOutside &&
        !showCloseBtn //not closing modal if showCloseBtn is true
      ) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // document.body.classList.add(styles.noScroll);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      // document.body.classList.remove(styles.noScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // document.body.classList.remove(styles.noScroll);
    };
  }, [isOpen, onClose]);

  //this code disables scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    // Cleanup on component unmount
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      id="static-modal"
      data-modal-backdrop="static"
      aria-hidden="true"
      className={`${
        isMobile && showFullScreen
          ? styles.modalContainer
          : "overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 flex justify-center items-center w-full md:inset-0 h-full max-h-full modalOpenBgDull"
      } ${modalClass} ${styles.zIndex}`}
    >
      <div
        ref={modalRef}
        className={`${
          isMobile && showFullScreen
            ? styles.modalCss
            : "relative md:max-w- md:max-h-full " + innerModalClass
        } ${modalBodyStyle}`}
        style={style}
      >
        {(showCloseBtn && isMobile && showFullScreen) ||
          (showCloseBtnNoFullScreen && (
            <button
              className={`${customCloseBtnClass} absolute top-1 right-4 text-black-500 text-4xl font-normal z-50 `}
              onClick={onClose}
            >
              &times;
            </button>
          ))}
        {children}
      </div>
    </div>
  );
};

export default Modal;
